import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import toast from 'react-hot-toast';
import {
  useAdminSubscriptionPlans, useCreateSubscriptionPlan, useUpdateSubscriptionPlan, useDeleteSubscriptionPlan,
} from '../../hooks/useMovie';
import { useAlert } from '../../components/ui/AlertModal';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '../../components/ui/dialog';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

const emptyForm = { name: '', billingCycle: 'Monthly', priceNaira: '', durationDays: '30', description: '', sortOrder: '0' };

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export default function AdminSubscriptionPlansPage() {
  usePageTitle('Subscription Plans · Kast');
  const { data: plans = [], isLoading } = useAdminSubscriptionPlans();
  const createMutation = useCreateSubscriptionPlan();
  const updateMutation = useUpdateSubscriptionPlan();
  const deleteMutation = useDeleteSubscriptionPlan();
  const { confirm } = useAlert();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const resetForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); };

  const startCreate = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };

  const startEdit = (plan) => {
    setEditingId(plan.Id);
    setForm({
      name: plan.Name,
      billingCycle: plan.BillingCycle,
      priceNaira: String(plan.PriceNaira),
      durationDays: String(plan.DurationDays),
      description: plan.Description || '',
      sortOrder: String(plan.SortOrder ?? 0),
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.priceNaira || !form.durationDays) {
      toast.error('Name, price, and duration are required.');
      return;
    }
    const payload = {
      name: form.name.trim(),
      billingCycle: form.billingCycle,
      priceNaira: parseFloat(form.priceNaira),
      durationDays: parseInt(form.durationDays, 10),
      description: form.description || null,
      sortOrder: parseInt(form.sortOrder || '0', 10),
    };
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, payload });
        toast.success('Plan updated.');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Plan created.');
      }
      resetForm();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save plan.'));
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await updateMutation.mutateAsync({ id: plan.Id, payload: { isActive: !plan.IsActive } });
      toast.success(plan.IsActive ? 'Plan deactivated.' : 'Plan activated.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update plan.'));
    }
  };

  const handleDelete = async (plan) => {
    const ok = await confirm(
      `Deactivate "${plan.Name}"? It will no longer be offered to new subscribers, but existing subscriptions on it keep working.`,
      { title: 'Deactivate Plan', variant: 'warning', isConfirm: true, confirmLabel: 'Deactivate' }
    );
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(plan.Id);
      toast.success('Plan deactivated.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to deactivate plan.'));
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
            <Icon icon="ph:credit-card-bold" className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Subscription Plans</h1>
            <p className="text-xs text-muted-foreground">Manage Monthly / Annual (or any other) plans shown on the Subscribe screen</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {plans.length > 0 && (
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary ring-1 ring-primary/25">
              {plans.length} plan{plans.length === 1 ? '' : 's'}
            </span>
          )}
          <Button size="sm" onClick={startCreate} className="gap-1.5">
            <Icon icon="ph:plus-bold" />
            Add Plan
          </Button>
        </div>
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : plans.length === 0 ? (
        <EmptyState icon="ph:credit-card-bold">No plans yet — add one above</EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.Id}
              className="flex flex-col gap-4 rounded-xl bg-white/[0.03] p-5 ring-1 ring-foreground/[0.06] transition-colors hover:bg-white/[0.05]"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-foreground">{plan.Name}</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">{plan.BillingCycle} · {plan.DurationDays} days</p>
                </div>
                {!plan.IsActive && (
                  <span className="shrink-0 rounded-full bg-destructive/10 px-2.5 py-0.5 text-[11px] font-semibold text-destructive ring-1 ring-destructive/25">
                    Inactive
                  </span>
                )}
              </div>

              <div className="text-2xl font-bold text-foreground">
                ₦{plan.PriceNaira?.toLocaleString()}
              </div>

              {plan.Description && <p className="text-sm text-muted-foreground">{plan.Description}</p>}

              <div className="mt-auto flex items-center gap-2 pt-1">
                <Button size="sm" variant="secondary" onClick={() => startEdit(plan)} className="flex-1 gap-1.5">
                  <Icon icon="ph:pencil-simple-bold" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleToggleActive(plan)}
                  disabled={updateMutation.isPending}
                  className="gap-1.5"
                >
                  <Icon icon={plan.IsActive ? 'ph:pause-bold' : 'ph:play-bold'} />
                  {plan.IsActive ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(plan)}
                  disabled={deleteMutation.isPending}
                  aria-label={`Delete ${plan.Name}`}
                  className="shrink-0 gap-1.5 bg-destructive/10 text-destructive ring-1 ring-destructive/25 hover:bg-destructive/20"
                >
                  <Icon icon="ph:trash-bold" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={(next) => { if (!next) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon icon={editingId ? 'ph:pencil-simple-bold' : 'ph:plus-bold'} />
              {editingId ? 'Edit Plan' : 'Add Plan'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="contents">
            <DialogBody>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name">
                  <input
                    className={inputClass}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Monthly"
                    autoFocus
                  />
                </Field>
                <Field label="Billing Cycle">
                  <Select value={form.billingCycle} onValueChange={(value) => setForm({ ...form, billingCycle: value })}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Price (₦)">
                  <input
                    className={inputClass}
                    type="number"
                    min="0"
                    value={form.priceNaira}
                    onChange={(e) => setForm({ ...form, priceNaira: e.target.value })}
                    placeholder="2000"
                  />
                </Field>
                <Field label="Duration (days)">
                  <input
                    className={inputClass}
                    type="number"
                    min="1"
                    value={form.durationDays}
                    onChange={(e) => setForm({ ...form, durationDays: e.target.value })}
                    placeholder="30"
                  />
                </Field>
              </div>
              <Field label="Description">
                <input
                  className={inputClass}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Billed every month. Cancel anytime."
                />
              </Field>
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={resetForm} className="gap-1.5">
                <Icon icon="ph:x-bold" />
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="gap-1.5">
                <Icon icon="ph:check-bold" />
                {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Plan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
